import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import React, { useMemo } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { tenantMlEngineerFragment } from './gql-operations';

const removeTenantMlEngineerMutation = graphql(`
  mutation removeTenantMlEngineer($tenantId: uuid!, $userId: uuid!) {
    deleteTenantMlEngineersByPk(tenantId: $tenantId, userId: $userId) {
      __typename
      tenantId
      userId
    }
  }
`);

type MlEngineersRow = {
  tenantId: string;
  userId: string;
  userEmail: string;
  userName: string;
  userFirstName: string;
  userLastName: string;
  addedAt: string;
};

const RemoveMlEngineerButton: React.FC<{
  userId: string;
  tenantId: string;
  userName: string;
}> = ({ userId, tenantId, userName }) => {
  const toast = useToast();
  const [deleteTenantMlEngineer] = useMutation(removeTenantMlEngineerMutation, {
    onError: () =>
      toast.error(`Failed to remove ${userName} as an ML engineer`),
    optimisticResponse: {
      deleteTenantMlEngineersByPk: {
        __typename: 'TenantMlEngineers' as const,
        tenantId,
        userId,
      },
    },
    update: () => {
      cacheUtils.updateFragment(
        {
          fragment: tenantMlEngineerFragment,
          key: { id: tenantId },
        },
        (existing) =>
          produce(existing, (draft) => {
            if (!draft) return;
            if (!draft.ml_engineers) return;

            draft.ml_engineers = draft.ml_engineers.filter(
              (e) => e.userId !== userId,
            );
          }),
      );
    },
  });

  const deleteMlEngineer = async () => {
    await deleteTenantMlEngineer({
      variables: {
        tenantId,
        userId,
      },
    });
  };

  return (
    <Button variant="ghost" onClick={deleteMlEngineer}>
      Remove
    </Button>
  );
};

const tenantMlEngineerColumns = new ColDefBuilder<MlEngineersRow>()
  .defaultSortable('userId', 'User ID')
  .defaultSortable('userName', 'User Name')
  .defaultSortable('userEmail', 'User Email')
  .dateSortable('addedAt', 'Added At')
  .colDef({
    header: 'Actions',
    cell: ({ row }) => (
      <RemoveMlEngineerButton
        tenantId={row.original.tenantId}
        userId={row.original.userId}
        userName={row.original.userName}
      />
    ),
  })
  .build();

export const TenantMlEngineersList: React.FC = () => {
  const tenantId = useTenantIdFromParams()!;

  const data = useCompleteFragment({
    fragment: tenantMlEngineerFragment,
    key: {
      id: tenantId,
    },
  });

  const rows = useMemo(() => {
    return (data?.ml_engineers ?? []).map<MlEngineersRow>((r) => ({
      tenantId,
      userId: r.user.id,
      userEmail: r.user.email,
      userFirstName: r.user.firstName,
      userLastName: r.user.lastName,
      userName: `${r.user.firstName} ${r.user.lastName}`,
      addedAt: r.createdAt,
    }));
  }, [data, tenantId]);

  return (
    <Stack>
      <span className="text-lg font-bold text-text-primary">
        Current ML Engineers
        <Text>
          These users have ML-engineer access to this organization and can
          export appointment data to GCS.
        </Text>
      </span>
      <DataTable
        data={rows}
        columns={tenantMlEngineerColumns}
        isPaginated={false}
        enableGlobalSearch
        initialSortingState={[
          {
            id: 'userName',
            desc: false,
          },
        ]}
      />
    </Stack>
  );
};
